import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Grid,
    Typography,
    Divider,
    MenuItem,
    InputAdornment,
    Box,
} from "@mui/material";
import {
    Person,
    Email,
    Phone,
    Home,
    LocationCity,
    MarkunreadMailbox,
    Business,
    Event,
    Edit,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

export default function ModifieShareholder({ open, handleClose, shareholder, company, onSave }) {
    const { t, i18n } = useTranslation(["Company", "Common"]);
    const userState = useSelector((state) => state.user);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        NoCellulaire: "",
        langue: "FR",
        department: "",
        PourcentageAction: "",
        isActive: true,
        estOperateur: false,
        adresse: "",
        NoAdresse: null,
        ville: "",
        codePostal: "",
        pays: "",
        province: "",
        region: "",
        storeNumber: "",
        companyName: "",
        openingDate: "",
        IdDepartement: "",
    });

    const [countriesData, setCountriesData] = useState([]);
    const [provincesData, setProvincesData] = useState([]);
    const [regionsData, setRegionsData] = useState([]);

    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingRegions, setLoadingRegions] = useState(false);

    const [departmentsData, setDepartmentsData] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);

    // -------------------
    // 1️⃣ Charger countries
    // -------------------
    useEffect(() => {
        if (!open) return;

        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Localization/GetCountries`, {
                    headers: { Authorization: `Bearer ${userState.token}` },
                    credentials: "include",
                });
                const data = await res.json();
                setCountriesData(data || []);
            } catch (err) {
                console.error("Erreur fetch countries", err);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, [open, userState.token]);

    // -------------------
    // 2️⃣ Initialiser formData avec shareholder & company
    // -------------------
    useEffect(() => {
        if (!shareholder || !company) return;

        setFormData(prev => ({
            ...prev,
            firstName: shareholder.firstName || "",
            lastName: shareholder.lastName || "",
            username: shareholder.username || "",
            email: shareholder.email || "",
            NoCellulaire: shareholder.NoCellulaire || "",
            langue: shareholder.langue || "FR",
            department: shareholder.department || "",
            PourcentageAction: shareholder.PourcentageAction || "",
            isActive: !shareholder.EstInactif,
            estOperateur: shareholder.estOperateur || false,
            adresse: shareholder.adresse || "",
            NoAdresse: shareholder.NoAdresse || null,
            ville: shareholder.ville || "",
            codePostal: shareholder.codePostal || "",
            pays: shareholder.pays || "",
            province: shareholder.province || "",
            region: shareholder.region || "",
            storeNumber: company.noAccpac || "",
            companyName: company.nom || "",
            openingDate: company.dateOuverture || "",
            IdDepartement: shareholder.idDepartement || "",
        }));
    }, [shareholder, company]);

    // -------------------
    // 3️⃣ Fetch provinces
    // -------------------
    useEffect(() => {
        const fetchProvinces = async () => {
            if (!formData.pays || !countriesData.length) return;

            try {
                setLoadingProvinces(true);
                setRegionsData([]);
                setFormData(prev => ({ ...prev, region: "" }));

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/Localization/GetProvinceByCountry/${formData.pays}`,
                    {
                        headers: { Authorization: `Bearer ${userState.token}` },
                        credentials: "include",
                    }
                );
                const data = await res.json();
                setProvincesData(data || []);
            } catch (err) {
                console.error("Erreur fetch provinces", err);
                setProvincesData([]);
            } finally {
                setLoadingProvinces(false);
            }
        };
        fetchProvinces();
    }, [formData.pays, countriesData, userState.token]);

    // -------------------
    // 4️⃣ Fetch regions
    // -------------------
    useEffect(() => {
        const fetchRegions = async () => {
            if (!formData.province || !provincesData.length) return;

            const selectedProvince = provincesData.find(p => p.noProvince === formData.province);
            if (!selectedProvince) return;

            try {
                setLoadingRegions(true);

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/Localization/GetRegionsByProvinces/${selectedProvince.noProvince}`,
                    {
                        headers: { Authorization: `Bearer ${userState.token}` },
                        credentials: "include",
                    }
                );
                const data = await res.json();
                setRegionsData(data || []);

                // Si formData.region est déjà défini et existe dans les options, on le garde
                if (formData.region && data.some(r => r.noRegion === formData.region)) {
                    setFormData(prev => ({ ...prev, region: prev.region }));
                }
                // Sinon, on prend la première option disponible
                else if (data?.length) {
                    setFormData(prev => ({ ...prev, region: data[0].noRegion }));
                }

            } catch (err) {
                console.error("Erreur fetch regions", err);
                setRegionsData([]);
            } finally {
                setLoadingRegions(false);
            }
        };

        fetchRegions();
    }, [formData.province, provincesData, userState.token]);

    // -------------------
    // 5️⃣ Fetch details shareholder
    // -------------------
    useEffect(() => {
        const fetchShareholderDetails = async () => {
            if (!open || !shareholder?.noEmploye) return;

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/employee/GetEmployeShareholder/${shareholder.noEmploye}`,
                    {
                        headers: { Authorization: `Bearer ${userState.token}` },
                        credentials: "include",
                    }
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!data) return;

                setFormData(prev => ({
                    ...prev,
                    adresse: data.adresse || "",
                    ville: data.ville || "",
                    codePostal: data.codePostal || "",
                    pays: data.noPays ?? "",
                    province: data.noProvince ?? "",
                    region: data.noRegion ?? "",
                    PourcentageAction: data.pourcentageAction ?? "",
                    NoAdresse: data.noAdresse ?? null,
                }));
            } catch (err) {
                console.error("Erreur chargement shareholder details", err);
            }
        };
        fetchShareholderDetails();
    }, [open, shareholder?.noEmploye, userState.token]);

    // -------------------
    // 6️⃣ Fetch departments
    // -------------------
    useEffect(() => {
        if (!open) return;

        const fetchDepartments = async () => {
            try {
                setLoadingDepartments(true);

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/Departement/GetActiveDepartements`,
                    {
                        headers: { Authorization: `Bearer ${userState.token}` },
                        credentials: "include",
                    }
                );

                if (!res.ok) {
                    console.error("Erreur API Departement");
                    return;
                }

                const data = await res.json();
                setDepartmentsData(data || []);

            } catch (err) {
                console.error("Erreur fetch departments", err);
                setDepartmentsData([]);
            } finally {
                setLoadingDepartments(false);
            }
        };

        fetchDepartments();
    }, [open, userState.token]);

    // -------------------
    // Handlers
    // -------------------
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        setFormData(prev => ({ ...prev, estOperateur: e.target.checked }));
    };


    const handleSubmit = async () => {
        const dto = {
            FirstName: formData.firstName,
            LastName: formData.lastName,
            Username: formData.username,
            Email: formData.email,
            NoCellulaire: formData.NoCellulaire,
            Langue: formData.langue,
            Department: formData.department,
            PourcentageAction: Number(formData.PourcentageAction),
            EstInactif: !formData.isActive,
            EstOperateur: formData.estOperateur,
            Adresse: formData.adresse,
            NoAdresse: formData.NoAdresse ? Number(formData.NoAdresse) : null,
            Ville: formData.ville,
            CodePostal: formData.codePostal,
            NoPays: formData.pays ? Number(formData.pays) : null,
            NoProvince: formData.province ? Number(formData.province) : null,
            NoRegion: formData.region ? Number(formData.region) : null,
            noCompany: company?.noCompagnie ?? null,
            NoEmploye: shareholder.noEmploye,
            IdDepartement: formData.IdDepartement ? Number(formData.IdDepartement) : null,
        };

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/Company/UpdateShareholder`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userState.token}`,
                },
                credentials: "include",
                body: JSON.stringify(dto),
            });

            const result = await res.json();

            if (res.ok && result.success) {
                onSave(result.data); // 👉 le parent gère tout
            } else {
                throw new Error(result.message);
            }

        } catch (err) {
            console.error(err);

            // 👉 On envoie l'erreur au parent aussi
            onSave(null, true);
        }
    };
    // -------------------
    // JSX
    // -------------------
    return (<>
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ borderBottom: "1px solid #eee" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Edit color="primary" />
                    <Typography variant="h5" fontWeight="bold">{t("Modification de l'actionnaire")}</Typography>
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 4 }}>
                    {company?.nom}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {/* ENTREPRISE */}
                <Typography variant="h6" sx={{ mb: 2 }}>{t("Informations entreprise")}</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <TextField label={t("Numéro magasin")} value={formData.storeNumber} fullWidth disabled
                            InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label={t("Nom entreprise")} value={formData.companyName} fullWidth disabled
                            InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label={t("Date ouverture")} type="date" value={formData.openingDate ? formData.openingDate.split("T")[0] : ""}
                            InputLabelProps={{ shrink: true }} fullWidth disabled
                            InputProps={{ startAdornment: <InputAdornment position="start"><Event /></InputAdornment> }} />
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                {/* INFORMATIONS PERSONNELLES */}
                <Typography variant="h6" sx={{ mb: 2 }}>{t("Informations personnelles")}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField label={t("Prénom")} name="firstName" value={formData.firstName} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField label={t("Nom")} name="lastName" value={formData.lastName} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField label="Username" name="username" value={formData.username} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField label="Téléphone" name="NoCellulaire" value={formData.NoCellulaire} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField label="% Action" name="PourcentageAction" type="number" value={formData.PourcentageAction} onChange={handleChange} fullWidth />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* ADRESSE */}
                <Typography variant="h6" sx={{ mb: 2 }}>{t("Adresse")}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField label={t("Adresse")} name="adresse" value={formData.adresse} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><Home /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label={t("Ville")} name="ville" value={formData.ville} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><LocationCity /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField label={t("Code postal")} name="codePostal" value={formData.codePostal} onChange={handleChange} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><MarkunreadMailbox /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField select label={t("Pays")} name="pays" value={formData.pays} onChange={handleChange} fullWidth disabled={loadingCountries}>
                            {countriesData.map(c => <MenuItem key={c.noPays} value={c.noPays}>{i18n.language === "FR" ? c.nomFr : c.nomEn}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField select label={t("Province")} name="province" value={formData.province} onChange={handleChange} fullWidth disabled={!formData.pays || loadingProvinces}>
                            {provincesData.map(p => <MenuItem key={p.noProvince} value={p.noProvince}>{i18n.language === "FR" ? p.nomFr : p.nomEn}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField select label={t("Région")} name="region" value={formData.region} onChange={handleChange} fullWidth disabled={!formData.province || loadingRegions}>
                            {regionsData.map(r => <MenuItem key={r.noRegion} value={r.noRegion}>{i18n.language === "FR" ? r.nomFr : r.nomEn}</MenuItem>)}
                        </TextField>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* Checkbox Exploitant */}
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.estOperateur}
                                    onChange={handleCheckboxChange}
                                />
                            }
                            label={t("Exploitant")}
                        />
                    </Grid>

                    {/* Dropdown Département */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label={t("Département")}
                            name="IdDepartement"
                            value={formData.IdDepartement}
                            onChange={handleChange}
                            fullWidth
                            disabled={formData.estOperateur || loadingDepartments}
                        >
                            {departmentsData.map(dep => (
                                <MenuItem key={dep.id} value={dep.id}>
                                    {i18n.language === "FR" ? dep.textFr : dep.textEn}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose}>{t("Annuler")}</Button>
                <Button variant="contained" size="large" onClick={handleSubmit}>{t("Sauvegarder")}</Button>
            </DialogActions>

        </Dialog>
        {/* -------------------
          Alert MUI hors du Dialog
          ------------------- */}
    </>
    );
}